<template>
  <div>
    <filter-manager
      v-model="model"
      class="mb-4 filter-manager-container"
      :display-advanced="false"
      :filters="filtersConfig"
      :search-text="isMobile ? 'Buscar' : ''"
      @providerType="onChangeFilterHandler"
      @search="onSearchHandler"
    />
    <custom-separator class="my-4" text="Resultado de Proveedores" />
    <div v-show="!!results.length" class="d-flex flex-row-reverse">
      <drop-down
        id="order-dropdown-btn"
        class="mb-2 order-dropdown"
        :icon="orderDropDownIcon"
        :items="orderItems"
        title="Ordenar"
      />
    </div>
    <custom-result-not-found v-if="results.length == 0" />
    <template v-else>
      <provider-approval-result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        class="mb-3"
        :data="row"
        :index="idx"
        @enter="onEnterHandler(row)"
        @on-history-load-more="onHistoryLoadMoreHandler(idx, row)"
        @on-open-close="getRequestHistoryHandler(idx, row, 1, 3)"
        @remove="onDeleteHandler(row)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import DropDown from 'dropdown';
import Pagination from 'pagination';
import {
  getProviderApprovalInitFilters as _getProviderApprovalInitFilters,
  getProviderOptions as _getProviderOptions,
  getProviderBiddingRequests as _getProviderBiddingRequests,
  getApprovalhistory as _getApprovalhistory,
  deleteApprovalRequest as _deleteApprovalRequest,
} from '@/services/provider/provider.api.js';
import store, { modulesName } from '@/store/index';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import ProviderApprovalResultRow from './ProviderApprovalResultRow.vue';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_HISTORY_PAGE_SIZE = 3;
const PROVIDER_CONTRACT_DOCUMENT_ROUTE_NAME = 'ProviderContractDocument';

export default {
  name: 'ProvidersApprovalTray',
  mixins: [alertMixin, storeMixin],
  components: {
    ProviderApprovalResultRow,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    DropDown,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    activeIdx: null,
    filterOptions: {
      fiscalYears: [],
      providerTypes: [],
      providers: [],
      statuses: [],
      documentStatuses: [],
      documentTypes: [],
      documentExpired: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
    orderActive: false,
    orderField: null,
  }),
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filtersConfig() {
      return [
        {
          name: 'fiscalYear',
          component: 'CustomDropDown',
          options: this.filterOptions.fiscalYears,
          placeholder: 'Año Fiscal',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3 mb-3',
        },
        {
          name: 'providerType',
          component: 'CustomDropDown',
          options: this.filterOptions.providerTypes,
          placeholder: 'Tipo de Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: this.userHasSingleCompany
            ? 'col-12 col-md-4'
            : 'col-12 col-md-3',
          disabled: this.userHasSingleCompany,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.userHasSingleCompany
            ? 'col-12 col-md-4'
            : 'col-12 col-md-3',
          clearable: true,
          disabled: this.userHasSingleCompany,
        },
        {
          name: 'estatus',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus de Solicitud',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          visible: !this.userHasSingleCompany,
        },
        {
          name: 'actionSearch',
          component: 'ActionButton',
          class: 'col-12 col-md-1 px-0 pr-3 btn-w-100',
          text: 'Buscar',
          variant: 'success',
          icon: 'fas fa-search',
          props: {
            text: 'Buscar',
            id: 'actionSearch',
          },
          visible: !this.isMobile,
        },
        {
          name: 'documentStatus',
          component: 'CustomDropDown',
          options: this.filterOptions.documentStatuses,
          placeholder: 'Estatus de Documento',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'documentType',
          component: 'CustomDropDown',
          options: this.filterOptions.documentTypes,
          placeholder: 'Tipo de Documento',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'documentExpired',
          component: 'CustomDropDown',
          options: this.filterOptions.documentExpired,
          placeholder: 'Documentos Vencidos',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'requestNumber',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          placeholder: 'Escriba Número de la solicitud',
          class: this.userHasSingleCompany
            ? 'col-12 col-md-4'
            : 'col-12 col-md-3',
        },
        {
          name: 'dateRange',
          component: 'CustomDateRange',
          placeholder: 'Rango de fecha vencimiento/término',
          class: 'col-12 col-md-3',
        },
      ];
    },
    userHasSingleCompany() {
      return this.userCompanies?.length === 1;
    },
    userCurrentCompany() {
      return this.userHasSingleCompany ? this.userCompanies[0] : null;
    },
    userCurrentCompanyType() {
      const company = this.userCompanies[0];
      return this.userHasSingleCompany
        ? {
            id: company.providerTypeId,
            name: company.providerTypeName,
          }
        : null;
    },
    userHasCompanies() {
      return this.userCompanies.length > 0;
    },
    orderItems() {
      return [
        {
          label: 'Por Fecha de Documento Sometido',
          click: () => {
            this.model.orderByLastDate = false;
            this.model.orderByDocumentExpirationDate = true;
            this.onSearchHandler();
          },
        },
        {
          label: 'Por Fecha de Último Estatus',
          click: () => {
            this.model.orderByDocumentExpirationDate = false;
            this.model.orderByLastDate = true;
            this.onSearchHandler();
          },
        },
      ];
    },
    orderDropDownIcon() {
      return this.orderActive ? 'fas fa-arrow-down-a-z' : 'fas fa-arrow-up-a-z';
    },
    isMobile() {
      return store.getters[`${modulesName.appModuleName}/getIsMobileDensity`];
    },
    currentPage() {
      return this.$route.params?.currentPage ?? 1;
    },
  },
  async mounted() {
    await this.getInitFilters();
    await this.getProviderOptions({
      companyType: null,
      status: null,
    });
    await this.onSearchHandler(this.currentPage);
    this.bindSearchHandler();
    this.bindOrderHandler();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (
        await _getProviderApprovalInitFilters()
      ).data;
      if (Object.values(filters).length && Object.values(collections).length) {
        this.model = {
          ...this.model,
          ...filters,
        };
        this.filterOptions = {
          ...this.filterOptions,
          ...collections,
          providers: this.userCompanies?.length ? this.userCompanies : [],
        };
      }
    },
    async getProviderOptions(payload) {
      const { data } = await _getProviderOptions(payload);
      if (!this.userHasCompanies) {
        this.filterOptions.providers = data;
      }
    },
    onChangeFilterHandler() {
      if (!this.userHasCompanies) {
        const filters = this.fillPayload();
        this.model.provider = null;
        this.getProviderOptions({
          companyType: filters.providerType,
          status: filters.estatus,
        });
      }
    },
    fillPayload(page) {
      const payload = {
        fiscalYearId: this.model.fiscalYear?.id,
        providerType: this.model.providerType?.id,
        companyId: this.model.provider?.id,
        estatus: this.model.estatus?.id,
        requestNumber: this.model.requestNumber,
        dateFrom: this.model.dateRange[0],
        dateTo: this.model.dateRange[1],
        documentStatusId: this.model.documentStatus?.id,
        documentTypeId: this.model.documentType?.id,
        documentExpired: this.model.documentExpired?.id,
        orderByDocumentExpirationDate: this.model.orderByDocumentExpirationDate,
        orderByLastDate: this.model.orderByLastDate,
        pageIndex: page ? page : 1,
        pageSize: 10,
      };
      return payload;
    },
    bindSearchHandler() {
      const btnSearch = document.getElementById('actionSearch');
      btnSearch.addEventListener('click', this.onSearchHandler);
    },
    bindOrderHandler() {
      const ddlOrder = document.getElementById('order-dropdown-btn');
      if (!ddlOrder) return;
      const btnOrden = ddlOrder.querySelector('button');
      btnOrden.addEventListener(
        'click',
        () => (this.orderActive = !this.orderActive)
      );
      btnOrden.addEventListener('focusout', () => (this.orderActive = false));
    },
    async searchProviderBiddingRequests(payload) {
      const res = (await _getProviderBiddingRequests(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    onSearchHandler(pageIndex = this.pagination.currentPage) {
      const payload = this.fillPayload(pageIndex);
      this.searchProviderBiddingRequests(payload);
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(page);
    },
    onClickDDL() {
      alert('dsa');
    },
    async getRequestHistoryHandler(idx, row, pageIndex, pageSize) {
      const { academicYearId, companyId, isOpen } = row;
      if (isOpen) {
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: false,
        }));
      } else {
        const payload = {
          academicYearId,
          companyId,
          pageIndex,
          pageSize,
        };
        const response = (await _getApprovalhistory(payload)).data;
        this.setPropsActiveRow(idx, { requestHistory: response });
      }
    },
    async onHistoryLoadMoreHandler(idx, row) {
      const { academicYearId, companyId } = row;
      const pageSize = row.requestHistory.count + DEFAULT_HISTORY_PAGE_SIZE;

      const payload = {
        academicYearId,
        companyId,
        pageIndex: 1,
        pageSize,
      };
      const response = (await _getApprovalhistory(payload)).data;
      this.setPropsActiveRow(idx, { requestHistory: response });
    },
    setPropsActiveRow(index, prop) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isOpen: idx === index,
        ...(idx === index ? prop : {}),
      }));
    },
    onEnterHandler({ academicYearId, companyId }) {
      let currentPage = this.pagination.currentPage;
      this.$router.push({
        name: PROVIDER_CONTRACT_DOCUMENT_ROUTE_NAME,
        params: {
          companyId,
          academicYearId,
          currentPage,
        },
      });
    },
    async onDeleteHandler({ academicYearId, companyId, requestNumber }) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Usted está seguro(a) que desea eliminar la solicitud ${requestNumber}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const { data } = await _deleteApprovalRequest(
        academicYearId,
        companyId
      ).catch((error) => {
        const message = error?.response?.data?.message ?? error.message;
        this.ShowToast('Alerta', `${message}`, 'error');
        return;
      });

      if (!data.hasError) {
        this.ShowToast(
          `Confirmación`,
          'La solicitud ha sido eliminada exitosamente.',
          'success'
        );

        await this.onSearchHandler();
      } else {
        this.ShowToast('Alerta', `${data.messageError}`, 'error');
        return;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.filter-manager-container ::v-deep {
  .btn-w-100 {
    width: 100%;
  }
}

.order-dropdown ::v-deep {
  .show > button {
    color: #fff !important;
    background-color: #2db782 !important;
    border-color: #2db782 !important;
  }
}
</style>
